/**
 * Reads a workspace's authored policies (Settings → Policies) from the
 * database into the client cache that `enforce.ts` applies.
 *
 * Every signed-in person loads them — at session start and on each workspace
 * switch (userStore.loadPermissions calls this) — so a rule an admin saves
 * holds for everyone, not only in the browser that saved it. The database
 * still decides for the acts it gates itself (spaces, type patterns —
 * migration 935); this cache lets the app leave out what a rule blocks.
 */

import { getTypedSupabaseClient } from '../typedSupabaseClient';
import {
  normalizePolicyRecord,
  setAuthoredPolicyCache,
  type AuthoredPolicyRecord,
} from './authoredPolicies';

/**
 * The workspace's authored policies (rows with a rule type), highest priority
 * first. Throws on a read error so the Policies page can show it.
 */
export async function fetchWorkspacePolicies(workspaceId: string): Promise<AuthoredPolicyRecord[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('policies')
    .select('*')
    .eq('workspace_id', workspaceId);
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as Record<string, unknown>[])
    .filter((row) => typeof row.rule_type === 'string' && row.rule_type !== '')
    .map((row) => normalizePolicyRecord(row, { priority: true }))
    .sort((a, b) => b.priority - a.priority || (b.updated_at ?? '').localeCompare(a.updated_at ?? ''));
}

let latestRequest = 0;

/**
 * Load the workspace's policies into the cache. With no workspace the cache is
 * emptied. A read that fails leaves the cache empty for this workspace rather
 * than keeping another workspace's rules; the database-gated acts still hold.
 */
export async function loadAuthoredPoliciesForWorkspace(workspaceId: string | null | undefined): Promise<void> {
  const request = ++latestRequest;
  if (!workspaceId) {
    setAuthoredPolicyCache([], null);
    return;
  }
  try {
    const policies = await fetchWorkspacePolicies(workspaceId);
    if (request === latestRequest) setAuthoredPolicyCache(policies, workspaceId);
  } catch (error) {
    if (request === latestRequest) setAuthoredPolicyCache([], workspaceId);
    console.warn('[authoredPolicies] could not load the workspace policies', error);
  }
}
